import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ConsultationModal from "@/components/ConsultationModal";
import { Play, Star, Quote, ArrowRight, MapPin, Stethoscope } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getYouTubeId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

function VideoCard({ video, onClick }) {
  const youtubeId = video.video_type === "youtube" ? getYouTubeId(video.video_url) : null;
  const thumbnail = video.thumbnail_url || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : null);

  return (
    <Card 
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden cursor-pointer group hover:shadow-lg transition-all"
      onClick={() => onClick(video)}
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative h-48 bg-slate-100">
        {thumbnail ? (
          <img src={thumbnail} alt={video.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-100 to-slate-100 flex items-center justify-center">
            <Play className="h-12 w-12 text-orange-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
            <Play className="h-8 w-8 text-orange-600 ml-1" />
          </div>
        </div>
        {video.featured && (
          <Badge className="absolute top-3 left-3 bg-orange-600 text-white border-0">Featured</Badge>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">{video.name}</h3>
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
          <MapPin className="h-3.5 w-3.5" />
          <span>{video.country}</span>
          <span className="text-slate-300">|</span>
          <Stethoscope className="h-3.5 w-3.5" />
          <span>{video.treatment}</span>
        </div>
        <p className="text-slate-600 text-sm line-clamp-2">{video.description}</p>
      </div>
    </Card>
  );
}

function VideoModal({ video, onClose }) {
  if (!video) return null;
  
  const youtubeId = video.video_type === "youtube" ? getYouTubeId(video.video_url) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
        >
          ×
        </button>
        <div className="aspect-video bg-black">
          {video.video_type === "youtube" && youtubeId ? (
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
              title={video.name}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video src={video.video_url} controls autoPlay className="w-full h-full" />
          )}
        </div>
        <div className="p-6">
          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{video.name}</h3>
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {video.country}</span>
            <span className="flex items-center gap-1"><Stethoscope className="h-4 w-4" /> {video.treatment}</span>
          </div>
          <p className="text-slate-600">{video.description}</p>
        </div>
      </div>
    </div>
  );
}

function TextTestimonials({ testimonials }) {
  return (
    <section className="py-16 bg-slate-50" data-testid="text-testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-4">Written Testimonials</h2>
          <p className="text-slate-500">Real experiences from our patients around the world</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((t) => (
            <Card key={t.id} className="bg-white rounded-2xl p-6 border border-slate-100 relative" data-testid={`text-testimonial-${t.id}`}>
              <Quote className="h-8 w-8 text-orange-100 absolute top-4 right-4" />
              <div className="flex gap-0.5 mb-4">
                {[...Array(t.rating)].map((_, i) => <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-6 relative z-10">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-orange-600 flex items-center justify-center text-sm font-bold text-white">
                  {t.avatar}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <p className="text-slate-400 text-sm">{t.country} - {t.treatment}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function TestimonialsPage() {
  const [videos, setVideos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/videos`),
      axios.get(`${API}/testimonials`)
    ]).then(([vRes, tRes]) => {
      setVideos(vRes.data);
      setTestimonials(tRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-bar" />
      </div>
    );
  }

  return (
    <div data-testid="testimonials-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-orange-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="bg-orange-600/10 text-orange-600 border-orange-600/20 mb-4 font-body text-xs uppercase tracking-wider">
              Patient Stories
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-4">
              Hear From Our <span className="text-orange-600">Happy Patients</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real stories from international patients who trusted Paxgrow for their medical journey to India
            </p>
          </div>
        </div>
      </section>

      {/* Video Testimonials */}
      {videos.length > 0 && (
        <section className="py-16" data-testid="video-testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-4">Video Testimonials</h2>
              <p className="text-slate-500">Watch our patients share their experience in their own words</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video) => (
                <VideoCard key={video.id} video={video} onClick={setSelectedVideo} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Text Testimonials */}
      <TextTestimonials testimonials={testimonials} />

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-orange-600 rounded-3xl p-10 md:p-16 text-center">
            <h2 className="font-heading text-3xl font-semibold text-white mb-4">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-orange-100 max-w-xl mx-auto mb-8">
              Join thousands of satisfied patients who chose India for world-class medical care at affordable prices.
            </p>
            <ConsultationModal
              trigger={
                <Button className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 font-semibold shadow-lg text-base" data-testid="testimonials-cta-btn">
                  Start Your Journey <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              }
            />
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />}
    </div>
  );
}
